import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { TabsList, TabsTrigger } from '@/components/molecules/Tabs';
import { cn } from '@/utils/cn';
import styles from './SegmentedTabs.module.scss';
import type { SegmentedTabsListProps } from './SegmentedTabs.types';

/**
 * Lista de segmentos tipo "píldora iOS" (el patrón de Aprendidas/Guardadas de
 * Progreso). Se monta DENTRO de un `<Tabs>` (Radix): renderiza el `TabsList`
 * con una píldora blanca que se desliza bajo el segmento activo. El consumidor
 * aporta el `<Tabs value onValueChange>` y sus `<TabsContent>`.
 *
 * La píldora se posiciona midiendo el trigger activo (refs + ResizeObserver),
 * y solo activa el tween tras el primer posicionamiento para que no "vuele"
 * desde 0,0 al montar.
 */
export function SegmentedTabsList({
  items,
  value,
  ariaLabel,
  className,
}: SegmentedTabsListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [pill, setPill] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [pillVisible, setPillVisible] = useState(false);
  const [tweenEnabled, setTweenEnabled] = useState(false);

  const measurePill = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const trigger = triggerRefs.current.get(value);
    if (!trigger) return;
    const lr = list.getBoundingClientRect();
    const tr = trigger.getBoundingClientRect();
    setPill({
      x: tr.left - lr.left,
      y: tr.top - lr.top,
      width: tr.width,
      height: tr.height,
    });
    setPillVisible(tr.width > 0 && tr.height > 0);
  }, [value]);

  useLayoutEffect(() => {
    measurePill();
  }, [measurePill]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const ro = new ResizeObserver(() => measurePill());
    ro.observe(list);
    triggerRefs.current.forEach((el) => ro.observe(el));
    window.addEventListener('resize', measurePill);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measurePill);
    };
  }, [measurePill, items]);

  useEffect(() => {
    if (!pillVisible || pill.width <= 0) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setTweenEnabled(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [pillVisible, pill.width, pill.height]);

  return (
    <TabsList
      ref={listRef}
      className={cn(styles.segmentedList, className)}
      aria-label={ariaLabel}
    >
      <motion.div
        className={styles.segmentPill}
        initial={false}
        animate={{ left: pill.x, top: pill.y, width: pill.width, height: pill.height }}
        style={{ opacity: pillVisible ? 1 : 0 }}
        transition={
          tweenEnabled
            ? { type: 'tween', duration: 0.22, ease: [0.32, 0.72, 0, 1] }
            : { duration: 0 }
        }
        aria-hidden
      />
      {items.map((item) => (
        <TabsTrigger
          key={item.value}
          value={item.value}
          ref={(el) => {
            if (el) triggerRefs.current.set(item.value, el);
            else triggerRefs.current.delete(item.value);
          }}
          className={styles.segmentTrigger}
        >
          <span className={styles.segmentTriggerLabel}>{item.label}</span>
        </TabsTrigger>
      ))}
    </TabsList>
  );
}

SegmentedTabsList.displayName = 'SegmentedTabsList';
